import { subDays } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  BasicSelectionBox,
  ContentCard,
  MasterStackBarChart,
  MetricCard,
  ScalrCarousel,
  ScalrEmptyBox,
} from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import BonusPayoutChart from '@/components/ui/charts/BonusPayoutChart'
import { chartColors, metricCardColors } from '@/constants/COLOR_PALETTE'
import {
  useDashboardBranchPerformance,
  useDashboardMetricCards,
  useKpiBonusProgress,
} from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import {
  addAlert,
  selectTenant,
  useAppDispatch,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'
import {
  IconBox,
  IconBrandSpeedtest,
  IconCashBanknote,
  IconChartArrowsVertical,
  IconChartBar,
  IconCurrencyDollar,
  IconPercentage,
  IconUsersGroup,
} from '@tabler/icons-react'

const MINI_METRIC_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[3] }}
      />
    ),
  },
  // {
  //   key: 'storeProfits' as const,
  //   header: t('clarity.pnl.storeProfits'),
  //   icon: (
  //     <IconCreditCard className="w-6 h-6" style={{ color: chartColors[0] }} />
  //   ),
  // },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[0] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[1] }} />
    ),
  },
  // {
  //   key: 'opEx' as const,
  //   header: t('clarity.pnl.opEx'),
  //   icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  // },
  // {
  //   key: 'rentAndOccupancy' as const,
  //   header: t('clarity.pnl.rentAndOccupancy'),
  //   icon: (
  //     <IconBuildingStore
  //       className="w-6 h-6"
  //       style={{ color: chartColors[5] }}
  //     />
  //   ),
  // },
]

const CHART_DATA_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[3] }}
      />
    ),
    tooltipDisplayType: 'money' as const,
  },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[0] }} />,
    tooltipDisplayType: 'percentage' as const,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[1] }} />
    ),
    tooltipDisplayType: 'percentage' as const,
  },
  // {
  //   key: 'opEx' as const,
  //   header: t('clarity.pnl.opEx'),
  //   icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  // },
  // {
  //   key: 'rentAndOccupancy' as const,
  //   header: t('clarity.pnl.rentAndOccupancy'),
  //   icon: (
  //     <IconBuildingStore
  //       className="w-6 h-6"
  //       style={{ color: chartColors[5] }}
  //     />
  //   ),
  // },
]

function DashboardContent() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  const { selectedBranch } = useReduxBaseFilter('dashboard')

  const tenant = useAppSelector(selectTenant)

  const dashboardDateRange = useMemo((): DateRange => {
    const to = tenant === 'dev' ? new Date(2024, 6, 26) : new Date()
    return { from: subDays(to, 15), to }
  }, [tenant])

  const [chartTypeFilter, setChartTypeFilter] = useState<string>('branch')

  const {
    metricCards,
    loading: metricCardsLoading,
    refetch: metricCardsRefetch,
  } = useDashboardMetricCards(
    dateToString(dashboardDateRange, 'from', 'yyyy-mm-dd'),
    dateToString(dashboardDateRange, 'to', 'yyyy-mm-dd'),
    selectedBranch,
    'daily'
  )

  const {
    branchPerformanceChartData,
    systemTotalPerformance,
    metricCardRecord,
    miniCardData,
    trendData,
    loading: branchPerformanceLoading,
    refetch: branchPerformanceRefetch,
  } = useDashboardBranchPerformance(
    selectedBranch,
    dateToString(dashboardDateRange, 'from', 'yyyy-mm-dd'),
    dateToString(dashboardDateRange, 'to', 'yyyy-mm-dd'),
    chartTypeFilter
  )

  const {
    bonusProgress,
    kpiRealizations: bonusKpiRealizations,
    loading: bonusProgressLoading,
    requiresQuarterRange,
  } = useKpiBonusProgress(
    dateToString(dashboardDateRange, 'to', 'yyyy-mm-dd'),
    dateToString(dashboardDateRange, 'to', 'yyyy-mm-dd')
  )

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle pageTitle>{t('clarity.dashboard')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="dashboard" hideDatePicker />

        <div className="space-y-6">
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              {/* TODO: remove isNewLogic after finalize migration fe logic to backend */}
              <MetricCard
                title={t('clarity.metrics.adns')}
                subTitle={t('clarity.metrics.adnsSubtitle')}
                mtdCurrContent={metricCards?.adns.mom}
                ytdCurrContent={metricCards?.adns.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.adns.value}
                type="money"
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.red500}
                loading={metricCardsLoading}
                chart={metricCards?.adns.chart}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.cogs')}
                subTitle={t('clarity.metrics.cogsSubtitle')}
                mtdCurrContent={metricCards?.cogs?.mom}
                ytdCurrContent={metricCards?.cogs?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.cogs?.value}
                type="money"
                icon={<IconCashBanknote color="white" size={20} />}
                color={metricCardColors.blue500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                chart={metricCards?.cogs?.chart}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.laborRatio')}
                subTitle={t('clarity.metrics.laborRatioSubtitle')}
                mtdCurrContent={metricCards?.labor?.mom}
                ytdCurrContent={metricCards?.labor?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.labor?.value}
                type="percentage"
                icon={<IconPercentage color="white" size={20} />}
                color={metricCardColors.green500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                chart={metricCards?.labor?.chart}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.speedOfService')}
                subTitle={t('clarity.metrics.speedOfServiceSubtitle')}
                mtdCurrContent={metricCards?.speed?.mom}
                ytdCurrContent={metricCards?.speed?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.speed?.value}
                type="seconds"
                icon={<IconBrandSpeedtest color="white" size={20} />}
                color={metricCardColors.orange500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                chart={metricCards?.speed?.chart}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.loyalty')}
                subTitle={t('clarity.metrics.loyaltySubtitle')}
                mtdCurrContent={metricCards?.loyalty?.mom}
                ytdCurrContent={metricCards?.loyalty?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.loyalty?.value}
                type="percentage"
                icon={<IconUsersGroup color="white" size={20} />}
                color={metricCardColors.blue300}
                loading={metricCardsLoading}
                chart={metricCards?.loyalty?.chart}
                isNewLogic
              />
            </ScalrCarousel>
          </div>

          <MasterStackBarChart
            title={t('dashboard.performanceByDivision')}
            metricCardRecord={metricCardRecord}
            miniCardData={miniCardData}
            trendData={trendData}
            loading={branchPerformanceLoading}
            miniMetricConfigs={MINI_METRIC_CONFIGS(t)}
            chartDataConfigs={CHART_DATA_CONFIGS(t)}
            customComponent={
              <BasicSelectionBox
                options={[
                  { value: 'branch', label: t('filters.dashboard.branch') },
                  { value: 'date', label: t('filters.dashboard.date') },
                ]}
                onValueChange={setChartTypeFilter}
                defaultValue={'branch'}
                // resetSelection={sizeFilterResetRef}
              />
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-1">
              <ContentCard
                title={t('dashboard.bonusPayoutProgress')}
                loading={bonusProgressLoading && !requiresQuarterRange}
                onViewMore={() => {
                  navigate('/targets/kpi-payout')
                }}
              >
                {requiresQuarterRange ? (
                  <ScalrEmptyBox
                    title={t('dashboard.bonusPayoutQuarterRangeTitle')}
                    message={t('dashboard.bonusPayoutQuarterRangeMessage')}
                    size="sm"
                  />
                ) : bonusProgress && bonusKpiRealizations.length === 0 ? (
                  <ScalrEmptyBox
                    title={t('dashboard.bonusPayoutNoKpisTitle')}
                    message={t('dashboard.bonusPayoutNoKpisMessage')}
                    size="sm"
                  />
                ) : (
                  <BonusPayoutChart
                    value={bonusProgress?.summary.currentBonusPayout ?? 0}
                    max={bonusProgress?.summary.maxAchievableBonus ?? 240}
                    unit="$"
                    kpiRealizations={bonusKpiRealizations}
                  />
                )}
              </ContentCard>
            </div>
            <div className="col-span-2">
              <ContentCard
                title={t('dashboard.scalrPulse')}
                loading={false}
                isBeta
              >
                Scalr Pulse
              </ContentCard>
            </div>
          </div>

          {/* <div>
            <DataTable
              columns={groupedCockpitMetricsColumns}
              data={[companyData, ...branchData].filter(
                (item): item is CockpitMetricsModel => item !== undefined
              )}
              selectedBranch={selectedBranch}
              cardTitle="Branch Metrics"
              cardDescription="General Branch Metrics"
              tableViewOption={true}
              loading={loading}
            />
          </div> */}
        </div>
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  return <DashboardContent />
}
